import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { CssBaseline, Container, Chip, Typography, Box, Badge, TextField, Skeleton } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import Api from '../../services/api';
import AppConnectorService, { normalizeProvider, normalizeEndpoint } from '../../services/appconnector.service';
import { FormattedMessage, injectIntl } from 'react-intl';
import styles from '../../styles/appconnector-styles';
import * as componentActions from '../../redux/actions/header-menu';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ProviderLogo from '../../components/app-connector/ProviderLogo';

class AppConnectorList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      openNotification: false,
      notificationVariant: 'error',
      notificationMessage: '',
      providers: [],
      endpointCounts: {},
      hoverCardId: null,
      searchValue: '',
    };
    this.closeNotification = this.closeNotification.bind(this);
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    this.setState({ loading: true });
    Promise.all([
      AppConnectorService.getProviders(),
      AppConnectorService.getEndpoints(),
    ]).then(([providersResult, endpointsResult]) => {
      const providers = providersResult.data.success ? (providersResult.data.data || []).map(normalizeProvider) : [];
      const endpoints = endpointsResult.data.success ? (endpointsResult.data.data || []).map(normalizeEndpoint) : [];

      // Count endpoints per provider
      const endpointCounts = {};
      endpoints.forEach(ep => {
        const pid = ep.appConnectorProviderId || ep.appconnectorproviderid || ep.externalProviderId || ep.externalproviderid;
        endpointCounts[pid] = (endpointCounts[pid] || 0) + 1;
      });

      this.setState({ loading: false, providers, endpointCounts });
    }).catch(err => {
      this.setState({ loading: false, openNotification: true, notificationVariant: 'error',
        notificationMessage: err.response?.data?.errors?.[0] || this.props.intl.formatMessage({ id: 'appconnector.common.error.loading' }) });
      Api.kickoff(err);
    });
  }

  handleProviderClick = (provider) => {
    this.props.history.push(`/${this.props.match.params.lang}/app-connectors/provider/${provider.id}/endpoints`);
  }

  closeNotification() {
    this.setState({ openNotification: false });
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const intl = this.props.intl;
    const { providers, endpointCounts } = this.state;

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open }, classes.actionsContent, { [classes.actionscontentShift]: actionsHeaderMenu.open })}>
          <div className={classes.drawerHeader} />
          <Container component='main' maxWidth='xl' style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />
            <div style={styles.contentWrapper}>
              <h1 style={styles.heading}>{intl.formatMessage({ id: 'app.connector.title' })}</h1>
              <p style={styles.subheading}>
                {intl.formatMessage({ id: 'app.connector.subtitle' })}. {intl.formatMessage({ id: 'appconnector.list.subtitle.cta' })}
              </p>

              <TextField
                placeholder={intl.formatMessage({ id: 'appconnector.list.search.placeholder' })}
                size="small"
                value={this.state.searchValue}
                onChange={e => this.setState({ searchValue: e.target.value })}
                sx={{ mb: 2, maxWidth: 400 }}
                fullWidth
              />

              {this.state.loading ? (
                <div style={styles.providerGrid}>
                  {[1, 2, 3].map(i => (
                    <div key={i} style={styles.providerCard}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <Skeleton variant="rounded" width={36} height={36} animation="wave" />
                        <Skeleton variant="text" width={120} height={28} animation="wave" />
                      </div>
                      <Skeleton variant="text" width="90%" height={16} animation="wave" />
                      <Skeleton variant="text" width="70%" height={16} animation="wave" />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <Skeleton variant="rounded" width={80} height={24} animation="wave" />
                        <Skeleton variant="rounded" width={60} height={24} animation="wave" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div style={styles.providerGrid}>
                {providers.filter(p => !this.state.searchValue || p.name.toLowerCase().includes(this.state.searchValue.toLowerCase()) || (p.description || '').toLowerCase().includes(this.state.searchValue.toLowerCase())).map(provider => {
                  const count = endpointCounts[provider.id] || 0;
                  return (
                    <div
                      key={provider.id}
                      style={{
                        ...styles.providerCard,
                        ...(this.state.hoverCardId === provider.id ? styles.providerCardHover : {})
                      }}
                      onClick={() => this.handleProviderClick(provider)}
                      onMouseEnter={() => this.setState({ hoverCardId: provider.id })}
                      onMouseLeave={() => this.setState({ hoverCardId: null })}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ProviderLogo slug={provider.slug} logoUrl={provider.logoUrl} size={36} />
                        <span style={styles.providerName}>{provider.name}</span>
                      </div>
                      <span style={styles.providerDescription}>{provider.description}</span>
                      <div style={styles.providerMeta}>
                        {(provider.isactive !== false && provider.isActive !== false) && <Chip label={intl.formatMessage({ id: 'appconnector.common.active' })} size="small" color="success" sx={{ fontSize: '11px' }} />}
                        <Chip label={provider.authtype || provider.authType || ''} size="small" variant="outlined" sx={{ fontSize: '11px' }} />
                        {count > 0 && (
                          <Chip label={intl.formatMessage({ id: count === 1 ? 'appconnector.list.endpoint.count.one' : 'appconnector.list.endpoint.count.many' }, { count })} size="small" color="primary" variant="outlined" sx={{ fontSize: '11px' }} />
                        )}
                        {count === 0 && (
                          <Chip label={intl.formatMessage({ id: 'appconnector.list.no.endpoint' })} size="small" variant="outlined" sx={{ fontSize: '11px', color: '#999' }} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {!this.state.loading && providers.length === 0 && (
                <div style={styles.emptyContainer}>
                  <IntegrationInstructionsIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                  <p style={styles.emptyText}>{intl.formatMessage({ id: 'appconnector.list.empty' })}</p>
                </div>
              )}
            </div>

            <CustomizedSnackbars variant={this.state.notificationVariant} message={this.state.notificationMessage}
              isOpen={this.state.openNotification} toClose={this.closeNotification} />
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

AppConnectorList.propTypes = { classes: PropTypes.object.isRequired };

const mapStateToProps = state => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
});

const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(AppConnectorList)));
